// src/pages/sessions/components/LiveSession/whiteboard/WhiteboardCanvas.jsx
import React, { useEffect, useRef, useState, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import WhiteboardContextMenu from './WhiteboardContextMenu';
import WhiteboardTextToolbar from './WhiteboardTextToolbar';

// Constants - fixed logical canvas size for consistent coordinates
const CANVAS_WIDTH = 1920;
const CANVAS_HEIGHT = 1080;

/**
 * Configure Fabric.js canvas styling to match Classroom Screen's look:
 * - Blue selection borders
 * - Circular corner handles  
 * - Semi-transparent blue selection rectangle
 */
function applyCanvasStyles(canvas) {
  // Selection rectangle (area select / marquee)
  canvas.selectionColor = 'rgba(99, 102, 241, 0.15)';       // Semi-transparent indigo
  canvas.selectionBorderColor = 'rgba(99, 102, 241, 0.6)';   // Indigo border
  canvas.selectionLineWidth = 1;
  canvas.selectionDashArray = [5, 5];                         // Dashed outline
}

/**
 * Style a Fabric.js object with Classroom Screen-like bounding box
 */
function applyObjectStyles(obj) {
  obj.set({
    borderColor: 'rgba(99, 102, 241, 0.8)',       // Indigo blue border
    cornerColor: '#ffffff',                         // White corner fill
    cornerStrokeColor: 'rgba(99, 102, 241, 0.8)', // Indigo corner stroke
    cornerSize: 10,
    cornerStyle: 'circle',                         // Circular handles
    transparentCorners: false,
    borderDashArray: null,                         // Solid selection border
    borderScaleFactor: 1.5,
    padding: Math.max(obj.padding || 0, 4),        // Keep larger padding (e.g. text notes)
  });
}

/**
 * Create a text widget with sticky-note style (yellowish background, generous size)
 */
function createTextWidget(fabric, pointer, color) {
  // Start with newlines so the note has visible height before typing
  const textbox = new fabric.Textbox('\n\n\n', {
    left: pointer.x,
    top: pointer.y,
    fontSize: 24,
    fill: color === '#FFFF00' ? '#000000' : color, // Avoid yellow text on yellow bg
    fontFamily: 'Arial, sans-serif',
    width: 280,
    minWidth: 150,
    editable: true,
    selectable: true,
    evented: true,
    backgroundColor: '#fff9c4',   // Sticky-note yellow
    padding: 24,
    editingBorderColor: 'rgba(99, 102, 241, 0.8)',
    rx: 2,
    ry: 2,
  });

  applyObjectStyles(textbox);
  return textbox;
}

/**
 * Ensure text note maintains minimum visual height (pad with trailing newlines)
 */
function ensureNoteMinHeight(textbox, minLines = 3) {
  const text = textbox.text || '';
  const lineCount = text.split('\n').length;
  if (lineCount < minLines) {
    const pad = '\n'.repeat(minLines - lineCount);
    textbox.set('text', text + pad);
  }
}

/**
 * WhiteboardCanvas - Fabric.js canvas component with improved UX
 * Matches Classroom Screen's visual quality for bounding boxes,
 * text widgets, image placeholders, and selection tools
 */
const WhiteboardCanvas = memo(({
  fabricRef,
  activeTool,
  activeColor,
  brushSize,
  canDraw,
  isTeacher,
  onPathCreated,
  onObjectModified,
  onSelectionChange,
  onStrokeStart,
  onStrokePoint,
  onStrokeEnd,
  onShapeCreated,
  onTextCreated,
  onObjectErased,
  onToolChange,       // NEW: callback to switch tool (for auto-switch)
  onBringToFront,     // NEW: z-order operations
  onSendToBack,
  onCopyWidget,
  onDeleteSelected,
  onAddImage,         // Callback to add image at position
}) => {
  const { t } = useTranslation();
  const canvasElRef = useRef(null);
  const containerRef = useRef(null);
  const [fabricLoaded, setFabricLoaded] = useState(false);
  const [fabricModule, setFabricModule] = useState(null);
  const [canvasReady, setCanvasReady] = useState(false);
  const prevTransformRef = useRef(null);
  const isDrawingRef = useRef(false);
  const strokeIdRef = useRef(null);
  const lastPointRef = useRef(null);
  
  // Shape drawing refs
  const shapeStartPointRef = useRef(null);
  const currentShapeRef = useRef(null);
  const isDrawingShapeRef = useRef(false);
  
  // Eraser drag ref
  const isErasingRef = useRef(false);
  
  // Context menu state
  const [contextMenu, setContextMenu] = useState({ visible: false, position: null, isLocked: false });
  
  // Text toolbar state
  const [textToolbar, setTextToolbar] = useState({ visible: false, position: null, textObject: null });
  
  // Image placement state - click position on canvas where user wants to place image
  const [imagePlacement, setImagePlacement] = useState(null); // { x, y } in canvas coords
  const imageFileInputRef = useRef(null);
  
  // Track selected object for overlays
  const selectedObjectRef = useRef(null);
  
  // Store callbacks in refs so Fabric.js handlers always get the latest version
  const onPathCreatedRef = useRef(onPathCreated);
  const onObjectModifiedRef = useRef(onObjectModified);
  const onSelectionChangeRef = useRef(onSelectionChange);
  const onStrokeStartRef = useRef(onStrokeStart);
  const onStrokePointRef = useRef(onStrokePoint);
  const onStrokeEndRef = useRef(onStrokeEnd);
  const onShapeCreatedRef = useRef(onShapeCreated);
  const onTextCreatedRef = useRef(onTextCreated);
  const onObjectErasedRef = useRef(onObjectErased);
  const onToolChangeRef = useRef(onToolChange);
  const onBringToFrontRef = useRef(onBringToFront);
  const onSendToBackRef = useRef(onSendToBack);
  const onCopyWidgetRef = useRef(onCopyWidget);
  const onDeleteSelectedRef = useRef(onDeleteSelected);
  const onAddImageRef = useRef(onAddImage);
  const activeToolRef = useRef(activeTool);
  const activeColorRef = useRef(activeColor);
  const brushSizeRef = useRef(brushSize);
  const canDrawRef = useRef(canDraw);
  
  // Keep refs updated
  useEffect(() => { onPathCreatedRef.current = onPathCreated; }, [onPathCreated]);
  useEffect(() => { onObjectModifiedRef.current = onObjectModified; }, [onObjectModified]);
  useEffect(() => { onSelectionChangeRef.current = onSelectionChange; }, [onSelectionChange]);
  useEffect(() => { onStrokeStartRef.current = onStrokeStart; }, [onStrokeStart]);
  useEffect(() => { onStrokePointRef.current = onStrokePoint; }, [onStrokePoint]);
  useEffect(() => { onStrokeEndRef.current = onStrokeEnd; }, [onStrokeEnd]);
  useEffect(() => { onShapeCreatedRef.current = onShapeCreated; }, [onShapeCreated]);
  useEffect(() => { onTextCreatedRef.current = onTextCreated; }, [onTextCreated]);
  useEffect(() => { onObjectErasedRef.current = onObjectErased; }, [onObjectErased]);
  useEffect(() => { onToolChangeRef.current = onToolChange; }, [onToolChange]);
  useEffect(() => { onBringToFrontRef.current = onBringToFront; }, [onBringToFront]);
  useEffect(() => { onSendToBackRef.current = onSendToBack; }, [onSendToBack]);
  useEffect(() => { onCopyWidgetRef.current = onCopyWidget; }, [onCopyWidget]);
  useEffect(() => { onDeleteSelectedRef.current = onDeleteSelected; }, [onDeleteSelected]);
  useEffect(() => { onAddImageRef.current = onAddImage; }, [onAddImage]);
  useEffect(() => { activeToolRef.current = activeTool; }, [activeTool]);
  useEffect(() => { activeColorRef.current = activeColor; }, [activeColor]);
  useEffect(() => { brushSizeRef.current = brushSize; }, [brushSize]);
  useEffect(() => { canDrawRef.current = canDraw; }, [canDraw]);

  // Dynamically import Fabric.js
  useEffect(() => {
    let cancelled = false;
    
    const loadFabric = async () => {
      try {
        const fabric = await import('fabric');
        if (!cancelled) {
          const mod = fabric.fabric || fabric.default || fabric;
          
          // Register custom properties so Fabric v6 includes them in
          // serialization (toJSON/toObject) AND restores them during
          // deserialization (loadFromJSON/fromObject) automatically.
          if (mod.FabricObject) {
            mod.FabricObject.customProperties = [
              ...(mod.FabricObject.customProperties || []),
              'objectId',
              'authorIdentity',
            ];
            
            // Set global control styling so ALL objects (including ActiveSelection)
            // get the indigo look without per-instance applyObjectStyles calls.
            Object.assign(mod.FabricObject.ownDefaults, {
              borderColor: 'rgba(99, 102, 241, 0.8)',
              cornerColor: '#ffffff',
              cornerStrokeColor: 'rgba(99, 102, 241, 0.8)',
              cornerSize: 10,
              cornerStyle: 'circle',
              transparentCorners: false,
              borderDashArray: null,
              borderScaleFactor: 1.5,
              padding: 4,
            });
          }
          
          setFabricModule(mod);
          setFabricLoaded(true);
        }
      } catch (err) {
        console.error('[WhiteboardCanvas] Failed to load Fabric.js:', err);
      }
    };
    
    loadFabric();
    
    return () => {
      cancelled = true;
    };
  }, []);

  // Resize canvas to fit container while maintaining aspect ratio
  const resizeCanvas = useCallback(() => {
    const canvas = fabricRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    
    // If container is too small (e.g. browser zoomed way in), do nothing.
    // Canvas keeps its last valid dimensions and objects stay intact.
    // When the user zooms back out, ResizeObserver fires and we resize normally.
    if (containerWidth < 50 || containerHeight < 50) return;
    
    const scaleX = containerWidth / CANVAS_WIDTH;
    const scaleY = containerHeight / CANVAS_HEIGHT;
    const scale = Math.min(scaleX, scaleY);
    
    const scaledWidth = CANVAS_WIDTH * scale;
    const scaledHeight = CANVAS_HEIGHT * scale;
    
    canvas.setDimensions({ width: scaledWidth, height: scaledHeight });
    canvas.setZoom(scale);
    canvas.calcOffset();
    canvas.requestRenderAll();
  }, [fabricRef]);

  // Generate stroke ID
  const generateStrokeId = useCallback(() => {
    return `stroke_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Helper: get screen position of a Fabric.js object for overlay positioning
  const getObjectScreenPosition = useCallback((obj) => {
    const canvas = fabricRef.current;
    if (!canvas || !obj) return null;

    const canvasEl = canvas.getElement();
    const canvasRect = canvasEl.getBoundingClientRect();
    const zoom = canvas.getZoom();
    
    const bound = obj.getBoundingRect();
    const scaleRatio = canvasRect.width / (CANVAS_WIDTH * zoom);
    
    return {
      x: canvasRect.left + (bound.left + bound.width / 2) * scaleRatio * zoom,
      y: canvasRect.top + bound.top * scaleRatio * zoom,
    };
  }, [fabricRef]);

  // Initialize Fabric canvas AND set up event handlers (all in one effect)
  useEffect(() => {
    if (!fabricLoaded || !fabricModule || !canvasElRef.current || !containerRef.current) return;
    if (fabricRef.current) return;
    
    const fabric = fabricModule;
    
    console.log('[WhiteboardCanvas] Initializing canvas and event handlers');
    
    const canvas = new fabric.Canvas(canvasElRef.current, {
      width: CANVAS_WIDTH,
      height: CANVAS_HEIGHT,
      backgroundColor: '#ffffff',
      selection: true,
      preserveObjectStacking: true,
      enableRetinaScaling: true,
    });
    
    fabricRef.current = canvas;
    canvas._fabricModule = fabric;
    
    // Apply Classroom Screen-like canvas styles
    applyCanvasStyles(canvas);
    
    // Configure free drawing brush
    canvas.freeDrawingBrush = new fabric.PencilBrush(canvas);
    canvas.freeDrawingBrush.color = activeColor || '#000000';
    canvas.freeDrawingBrush.width = brushSize || 3;
    canvas.freeDrawingBrush.decimate = 2;
    canvas.freeDrawingBrush.strokeLineCap = 'round';
    canvas.freeDrawingBrush.strokeLineJoin = 'round';
    canvas.isDrawingMode = activeTool === 'marker' || activeTool === 'brush' || activeTool === 'highlighter';
    
    // Helper to get pointer position (Fabric.js v5/v6 compatible)
    const getPointer = (e) => {
      let pointer = e.scenePoint || e.viewportPoint || e.pointer;
      if (!pointer && e.e && typeof canvas.getPointer === 'function') {
        pointer = canvas.getPointer(e.e);
      }
      if (!pointer && e.e) {
        const rect = canvas.getElement().getBoundingClientRect();
        const zoom = canvas.getZoom();
        pointer = {
          x: (e.e.clientX - rect.left) / zoom,
          y: (e.e.clientY - rect.top) / zoom
        };
      }
      return pointer;
    };
    
    // ===== MOUSE DOWN =====
    canvas.on('mouse:down', (e) => {
      const tool = activeToolRef.current;
      const pointer = getPointer(e);
      if (!pointer) return;
      
      // Close context menu on any left click
      if (!e.e || e.e.button !== 2) {
        setContextMenu({ visible: false, position: null, isLocked: false });
      }
      
      // Handle selection transform capture
      if (e.target && e.target.objectId) {
        prevTransformRef.current = {
          objectId: e.target.objectId,
          left: e.target.left,
          top: e.target.top,
          scaleX: e.target.scaleX,
          scaleY: e.target.scaleY,
          angle: e.target.angle,
          stroke: e.target.stroke,
          strokeWidth: e.target.strokeWidth,
        };
      }
      
      // ===== RIGHT-CLICK CONTEXT MENU =====
      if (e.e && e.e.button === 2 && e.target && e.target.objectId) {
        e.e.preventDefault();
        e.e.stopPropagation();
        
        canvas.setActiveObject(e.target);
        canvas.renderAll();
        
        setContextMenu({
          visible: true,
          position: { x: e.e.clientX, y: e.e.clientY },
          isLocked: !!e.target.lockMovementX,
        });
        return;
      }
      
      // ===== ERASER =====
      // Block all tool actions when drawing is disabled (e.g. student permissions revoked)
      if (!canDrawRef.current) return;
      
      if (tool === 'eraser') {
        isErasingRef.current = true;
        const objects = canvas.getObjects();
        for (let i = objects.length - 1; i >= 0; i--) {
          const obj = objects[i];
          if (obj.containsPoint(pointer)) {
            if (onObjectErasedRef.current) {
              onObjectErasedRef.current(obj);
            }
            break;
          }
        }
        return;
      }
      
      // ===== TEXT TOOL =====
      if (tool === 'text') {
        const color = activeColorRef.current;
        const textbox = createTextWidget(fabric, pointer, color);
        
        canvas.add(textbox);
        canvas.setActiveObject(textbox);
        
        // Enter editing mode immediately — selectAll replaces the placeholder newlines
        textbox.enterEditing();
        textbox.selectAll();
        
        canvas.renderAll();
        
        // Register object when editing is done
        textbox.on('editing:exited', () => {
          if (textbox.text && textbox.text.trim()) {
            // Pad with trailing newlines so the note background stays a nice size
            ensureNoteMinHeight(textbox, 3);
            canvas.renderAll();
            if (onTextCreatedRef.current) {
              onTextCreatedRef.current(textbox);
            }
          } else {
            canvas.remove(textbox);
            canvas.renderAll();
          }
          // Reset cursor — Fabric overrides it to 'text' while IText is
          // in editing mode, so the earlier switch to 'select' gets eaten.
          canvas.defaultCursor = 'default';
          canvas.hoverCursor = 'move';
          if (canvas.upperCanvasEl) canvas.upperCanvasEl.style.cursor = 'default';
        });
        
        // Auto-switch to Select tool after placing text
        if (onToolChangeRef.current) {
          onToolChangeRef.current('select');
        }
        return;
      }
      
      // ===== IMAGE TOOL =====
      if (tool === 'image') {
        // Record click position and show the image upload modal
        setImagePlacement({ x: pointer.x, y: pointer.y });
        return;
      }
      
      // ===== SHAPE TOOLS =====
      const shapeTools = ['line', 'arrow', 'rectangle', 'circle', 'filledRectangle', 'filledCircle'];
      if (shapeTools.includes(tool)) {
        shapeStartPointRef.current = pointer;
        isDrawingShapeRef.current = true;
        
        let shape = null;
        const color = activeColorRef.current;
        const strokeWidth = brushSizeRef.current;
        const isFilled = tool === 'filledRectangle' || tool === 'filledCircle';
        
        if (tool === 'line' || tool === 'arrow') {
          shape = new fabric.Line([pointer.x, pointer.y, pointer.x, pointer.y], {
            stroke: color,
            strokeWidth: strokeWidth,
            strokeLineCap: 'round',
            selectable: false,
            evented: false,
          });
        } else if (tool === 'rectangle' || tool === 'filledRectangle') {
          shape = new fabric.Rect({
            left: pointer.x,
            top: pointer.y,
            width: 0,
            height: 0,
            stroke: color,
            strokeWidth: isFilled ? 0 : strokeWidth,
            fill: isFilled ? color : 'transparent',
            originX: 'left',
            originY: 'top',
            selectable: false,
            evented: false,
          });
        } else if (tool === 'circle' || tool === 'filledCircle') {
          shape = new fabric.Ellipse({
            left: pointer.x,
            top: pointer.y,
            rx: 0,
            ry: 0,
            stroke: color,
            strokeWidth: isFilled ? 0 : strokeWidth,
            fill: isFilled ? color : 'transparent',
            originX: 'left',
            originY: 'top',
            selectable: false,
            evented: false,
          });
        }
        
        if (shape) {
          currentShapeRef.current = shape;
          canvas.add(shape);
          canvas.renderAll();
        }
        return;
      }
      
      // ===== LIVE STROKE STREAMING (marker/brush/highlighter) =====
      if (canvas.isDrawingMode && (tool === 'marker' || tool === 'brush' || tool === 'highlighter')) {
        isDrawingRef.current = true;
        strokeIdRef.current = generateStrokeId();
        lastPointRef.current = pointer;
        
        const isHighlighter = tool === 'highlighter';
        const isBrush = tool === 'brush';
        let strokeWidth = brushSizeRef.current;
        if (isHighlighter) strokeWidth = brushSizeRef.current * 4;
        else if (isBrush) strokeWidth = brushSizeRef.current * 3;
        
        if (onStrokeStartRef.current) {
          onStrokeStartRef.current({
            strokeId: strokeIdRef.current,
            point: pointer,
            color: activeColorRef.current,
            width: strokeWidth,
            opacity: isHighlighter ? 0.3 : (isBrush ? 0.6 : 1),
          });
        }
      }
    });
    
    // ===== MOUSE MOVE =====
    canvas.on('mouse:move', (e) => {
      const tool = activeToolRef.current;
      const pointer = getPointer(e);
      if (!pointer) return;
      
      // ===== ERASER DRAG =====
      if (isErasingRef.current && tool === 'eraser' && canDrawRef.current) {
        const objects = canvas.getObjects();
        for (let i = objects.length - 1; i >= 0; i--) {
          const obj = objects[i];
          if (obj.containsPoint(pointer)) {
            if (onObjectErasedRef.current) {
              onObjectErasedRef.current(obj);
            }
            break;
          }
        }
        return;
      }
      
      // ===== SHAPE DRAWING =====
      if (isDrawingShapeRef.current && currentShapeRef.current && shapeStartPointRef.current) {
        const startX = shapeStartPointRef.current.x;
        const startY = shapeStartPointRef.current.y;
        const shape = currentShapeRef.current;
        
        if (tool === 'line' || tool === 'arrow') {
          shape.set({ x2: pointer.x, y2: pointer.y });
        } else if (tool === 'rectangle' || tool === 'filledRectangle') {
          const left = Math.min(startX, pointer.x);
          const top = Math.min(startY, pointer.y);
          const width = Math.abs(pointer.x - startX);
          const height = Math.abs(pointer.y - startY);
          shape.set({ left, top, width, height });
        } else if (tool === 'circle' || tool === 'filledCircle') {
          const left = Math.min(startX, pointer.x);
          const top = Math.min(startY, pointer.y);
          const width = Math.abs(pointer.x - startX);
          const height = Math.abs(pointer.y - startY);
          shape.set({
            left: left,
            top: top,
            rx: width / 2,
            ry: height / 2,
          });
        }
        
        shape.setCoords();
        canvas.renderAll();
        return;
      }
      
      // ===== STROKE STREAMING =====
      if (!isDrawingRef.current || !strokeIdRef.current) return;
      
      if (lastPointRef.current) {
        const dx = pointer.x - lastPointRef.current.x;
        const dy = pointer.y - lastPointRef.current.y;
        if (dx * dx + dy * dy < 9) return;
      }
      
      lastPointRef.current = pointer;
      
      if (onStrokePointRef.current) {
        onStrokePointRef.current({
          strokeId: strokeIdRef.current,
          point: pointer,
        });
      }
    });
    
    // ===== MOUSE UP =====
    canvas.on('mouse:up', (e) => {
      const tool = activeToolRef.current;
      const pointer = getPointer(e);
      
      if (isErasingRef.current) {
        isErasingRef.current = false;
        return;
      }
      
      // ===== FINISH SHAPE =====
      if (isDrawingShapeRef.current && currentShapeRef.current) {
        const shape = currentShapeRef.current;
        
        let isValid = false;
        if (tool === 'line' || tool === 'arrow') {
          const dx = shape.x2 - shape.x1;
          const dy = shape.y2 - shape.y1;
          isValid = Math.sqrt(dx * dx + dy * dy) > 5;
        } else if (tool === 'rectangle' || tool === 'filledRectangle') {
          isValid = shape.width > 5 && shape.height > 5;
        } else if (tool === 'circle' || tool === 'filledCircle') {
          isValid = shape.rx > 5 || shape.ry > 5;
        }
        
        if (isValid) {
          if (tool === 'arrow' && pointer) {
            canvas.remove(shape);
            
            const arrowHead = createArrowHead(
              fabric, 
              shape.x1, shape.y1, 
              shape.x2, shape.y2, 
              activeColorRef.current,
              brushSizeRef.current
            );
            
            const arrowLine = new fabric.Line([shape.x1, shape.y1, shape.x2, shape.y2], {
              stroke: activeColorRef.current,
              strokeWidth: brushSizeRef.current,
              strokeLineCap: 'round',
            });
            
            const arrowGroup = new fabric.Group([arrowLine, arrowHead], {
              selectable: true,
              evented: true,
            });
            
            applyObjectStyles(arrowGroup);
            canvas.add(arrowGroup);
            
            if (onShapeCreatedRef.current) {
              onShapeCreatedRef.current(arrowGroup);
            }
          } else {
            shape.selectable = true;
            shape.evented = true;
            applyObjectStyles(shape);
            
            if (onShapeCreatedRef.current) {
              onShapeCreatedRef.current(shape);
            }
          }
        } else {
          canvas.remove(shape);
        }
        
        canvas.renderAll();
        currentShapeRef.current = null;
        shapeStartPointRef.current = null;
        isDrawingShapeRef.current = false;
        return;
      }
      
      // ===== END STROKE STREAMING =====
      if (isDrawingRef.current && strokeIdRef.current) {
        if (onStrokeEndRef.current) {
          onStrokeEndRef.current({
            strokeId: strokeIdRef.current,
          });
        }
      }
      isDrawingRef.current = false;
      strokeIdRef.current = null;
      lastPointRef.current = null;
    });
    
    // Helper to create arrow head
    const createArrowHead = (fabric, x1, y1, x2, y2, color, strokeWidth) => {
      const angle = Math.atan2(y2 - y1, x2 - x1);
      const headLength = Math.max(15, strokeWidth * 4);
      const headAngle = Math.PI / 6;
      
      const points = [
        { x: x2, y: y2 },
        { 
          x: x2 - headLength * Math.cos(angle - headAngle), 
          y: y2 - headLength * Math.sin(angle - headAngle) 
        },
        { 
          x: x2 - headLength * Math.cos(angle + headAngle), 
          y: y2 - headLength * Math.sin(angle + headAngle) 
        },
      ];
      
      return new fabric.Polygon(points, {
        fill: color,
        stroke: color,
        strokeWidth: 1,
      });
    };
    
    // ===== PATH CREATED (final stroke) =====
    canvas.on('path:created', (e) => {
      const path = e.path;
      
      if (activeToolRef.current === 'highlighter') {
        path.opacity = 0.3;
        path.strokeLineCap = 'round';
        path.strokeLineJoin = 'round';
      }
      
      // Apply Classroom Screen-style bounding box to new paths
      applyObjectStyles(path);
      
      if (onPathCreatedRef.current) {
        onPathCreatedRef.current(path);
      }
    });
    
    // ===== OBJECT MODIFIED =====
    canvas.on('object:modified', (e) => {
      const obj = e.target;
      if (!obj) return;
      
      // ActiveSelection (multi-select group): broadcast each child's final position
      if (obj.type === 'activeSelection' || obj.type === 'activeselection') {
        const objects = obj.getObjects ? obj.getObjects() : [];
        if (onObjectModifiedRef.current) {
          objects.forEach(child => {
            if (!child.objectId) return;
            
            // Compute child's absolute transform (group transform × child transform)
            const matrix = child.calcTransformMatrix();
            const decomposed = fabric.util.qrDecompose(matrix);
            
            const newTransform = {
              left: decomposed.translateX,
              top: decomposed.translateY,
              scaleX: decomposed.scaleX,
              scaleY: decomposed.scaleY,
              angle: decomposed.angle,
              stroke: child.stroke,
              strokeWidth: child.strokeWidth,
            };
            
            if (child.type === 'textbox' || child.type === 'i-text' || child.type === 'text') {
              Object.assign(newTransform, {
                text: child.text,
                fill: child.fill,
                fontFamily: child.fontFamily,
                fontSize: child.fontSize,
                fontWeight: child.fontWeight,
                fontStyle: child.fontStyle,
                textAlign: child.textAlign,
                underline: child.underline,
                backgroundColor: child.backgroundColor,
                width: child.width,
              });
            }
            
            onObjectModifiedRef.current(child, newTransform, null);
          });
        }
        prevTransformRef.current = null;
        return;
      }
      
      if (!obj.objectId) return;
      
      const newTransform = {
        left: obj.left,
        top: obj.top,
        scaleX: obj.scaleX,
        scaleY: obj.scaleY,
        angle: obj.angle,
        stroke: obj.stroke,
        strokeWidth: obj.strokeWidth,
      };
      
      // Include text-specific properties for text objects
      if (obj.type === 'textbox' || obj.type === 'i-text' || obj.type === 'text') {
        Object.assign(newTransform, {
          text: obj.text,
          fill: obj.fill,
          fontFamily: obj.fontFamily,
          fontSize: obj.fontSize,
          fontWeight: obj.fontWeight,
          fontStyle: obj.fontStyle,
          textAlign: obj.textAlign,
          underline: obj.underline,
          backgroundColor: obj.backgroundColor,
          width: obj.width,
        });
      }
      
      if (onObjectModifiedRef.current) {
        onObjectModifiedRef.current(obj, newTransform, prevTransformRef.current);
      }
      
      prevTransformRef.current = null;
    });
    
    // ===== TEXT CONTENT SYNC =====
    // Fabric.js does NOT fire object:modified when text content changes via typing.
    // Listen for text:editing:exited to sync content after the user finishes editing.
    canvas.on('text:editing:exited', (e) => {
      const obj = e.target;
      if (!obj || !obj.objectId) return;
      
      // Sync text content + all text-specific properties
      if (onObjectModifiedRef.current) {
        onObjectModifiedRef.current(obj, {
          left: obj.left,
          top: obj.top,
          scaleX: obj.scaleX,
          scaleY: obj.scaleY,
          angle: obj.angle,
          text: obj.text,
          fill: obj.fill,
          fontFamily: obj.fontFamily,
          fontSize: obj.fontSize,
          fontWeight: obj.fontWeight,
          fontStyle: obj.fontStyle,
          textAlign: obj.textAlign,
          underline: obj.underline,
          backgroundColor: obj.backgroundColor,
          width: obj.width,
        }, null);
      }
      
      // Restore selection-mode cursor after Fabric's text editing overrode it
      canvas.defaultCursor = 'default';
      canvas.hoverCursor = 'move';
      if (canvas.upperCanvasEl) canvas.upperCanvasEl.style.cursor = 'default';
    });
    
    // ===== SELECTION EVENTS =====
    const handleSelection = (e) => {
      const selected = e.selected || [];
      selectedObjectRef.current = selected.length === 1 ? selected[0] : null;
      
      // Style multi-selection group (ActiveSelection) with same indigo look
      const activeObj = canvas.getActiveObject();
      if (activeObj && activeObj.type === 'activeSelection') {
        applyObjectStyles(activeObj);
        // Also style every child so their individual borders match
        const children = activeObj.getObjects ? activeObj.getObjects() : [];
        children.forEach(child => applyObjectStyles(child));
        canvas.requestRenderAll();
      }
      
      if (onSelectionChangeRef.current) {
        onSelectionChangeRef.current(selected);
      }
      
      // Show text toolbar for text objects
      if (selected.length === 1 && (selected[0].type === 'textbox' || selected[0].type === 'i-text' || selected[0].type === 'text')) {
        const pos = getObjectScreenPosition(selected[0]);
        if (pos) {
          setTextToolbar({ visible: true, position: pos, textObject: selected[0] });
        }
      } else {
        setTextToolbar({ visible: false, position: null, textObject: null });
      }
    };

    canvas.on('selection:created', handleSelection);
    canvas.on('selection:updated', handleSelection);
    
    canvas.on('selection:cleared', () => {
      selectedObjectRef.current = null;
      setTextToolbar({ visible: false, position: null, textObject: null });
      setContextMenu({ visible: false, position: null, isLocked: false });
      
      if (onSelectionChangeRef.current) {
        onSelectionChangeRef.current([]);
      }
    });

    // Prevent default context menu on canvas
    const canvasEl = canvas.getElement();
    const upperCanvas = canvas.upperCanvasEl || canvasEl;
    const preventContextMenu = (e) => e.preventDefault();
    upperCanvas.addEventListener('contextmenu', preventContextMenu);
    canvasEl.addEventListener('contextmenu', preventContextMenu);
    
    // Initial resize
    resizeCanvas();
    setCanvasReady(true);
    
    return () => {
      console.log('[WhiteboardCanvas] Disposing canvas');
      setCanvasReady(false);
      upperCanvas.removeEventListener('contextmenu', preventContextMenu);
      canvasEl.removeEventListener('contextmenu', preventContextMenu);
      canvas.dispose();
      fabricRef.current = null;
    };
  }, [fabricLoaded, fabricModule, fabricRef, resizeCanvas, generateStrokeId, getObjectScreenPosition]);

  // Handle resize — debounced to handle browser zoom transitions gracefully
  useEffect(() => {
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => resizeCanvas(), 50);
    };
    
    window.addEventListener('resize', handleResize);
    
    const container = containerRef.current;
    let resizeObserver;
    if (container && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(container);
    }
    
    return () => {
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', handleResize);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [resizeCanvas]);

  // Update tool mode
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !canvasReady) return;
    
    const fabric = canvas._fabricModule;
    if (!fabric) return;
    
    const shapeTools = ['line', 'arrow', 'rectangle', 'circle', 'filledRectangle', 'filledCircle'];
    
    // Dot cursor for all drawing tools
    const dotCursor = 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'16\' height=\'16\' viewBox=\'0 0 16 16\'%3E%3Ccircle cx=\'8\' cy=\'8\' r=\'4\' fill=\'%23333\' stroke=\'%23fff\' stroke-width=\'1\'/%3E%3C/svg%3E") 8 8, crosshair';
    
    switch (activeTool) {
      case 'select':
        canvas.isDrawingMode = false;
        canvas.selection = true;
        canvas.skipTargetFind = false;
        canvas.defaultCursor = 'default';
        canvas.hoverCursor = 'move';
        // Force cursor on the actual DOM element — otherwise the browser
        // keeps the previous CSS cursor until the next native mouse event
        if (canvas.upperCanvasEl) canvas.upperCanvasEl.style.cursor = 'default';
        canvas.forEachObject(o => {
          if (!o.lockMovementX) {
            o.selectable = canDraw;
            o.evented = canDraw;
          }
        });
        break;
        
      case 'marker':
        canvas.isDrawingMode = true;
        canvas.selection = false;
        canvas.skipTargetFind = true;
        canvas.freeDrawingBrush = new fabric.PencilBrush(canvas);
        canvas.freeDrawingBrush.color = activeColor;
        canvas.freeDrawingBrush.width = brushSize;
        canvas.freeDrawingBrush.decimate = 4;
        canvas.freeDrawingBrush.strokeLineCap = 'round';
        canvas.freeDrawingBrush.strokeLineJoin = 'round';
        canvas.freeDrawingCursor = dotCursor;
        break;
        
      case 'brush':
        canvas.isDrawingMode = true;
        canvas.selection = false;
        canvas.skipTargetFind = true;
        canvas.freeDrawingBrush = new fabric.PencilBrush(canvas);
        canvas.freeDrawingBrush.width = brushSize * 3;
        canvas.freeDrawingBrush.decimate = 2;
        canvas.freeDrawingBrush.strokeLineCap = 'round';
        canvas.freeDrawingBrush.strokeLineJoin = 'round';
        canvas.freeDrawingBrush.color = activeColor.startsWith('#')
          ? activeColor + 'cc'
          : activeColor;
        canvas.freeDrawingCursor = dotCursor;
        break;
        
      case 'highlighter':
        canvas.isDrawingMode = true;
        canvas.selection = false;
        canvas.skipTargetFind = true;
        canvas.freeDrawingBrush = new fabric.PencilBrush(canvas);
        canvas.freeDrawingBrush.color = activeColor;
        canvas.freeDrawingBrush.width = brushSize * 4;
        canvas.freeDrawingBrush.decimate = 4;
        canvas.freeDrawingBrush.strokeLineCap = 'square';
        canvas.freeDrawingBrush.strokeLineJoin = 'miter';
        canvas.freeDrawingCursor = dotCursor;
        break;
        
      case 'eraser':
        canvas.isDrawingMode = false;
        canvas.selection = false;
        canvas.skipTargetFind = false;
        canvas.defaultCursor = dotCursor;
        canvas.hoverCursor = dotCursor;
        canvas.forEachObject(o => {
          o.selectable = false;
          o.evented = true;
        });
        break;
        
      case 'line':
      case 'arrow':
      case 'rectangle':
      case 'circle':
      case 'filledRectangle':
      case 'filledCircle':
        canvas.isDrawingMode = false;
        canvas.selection = false;
        canvas.skipTargetFind = true;
        canvas.defaultCursor = dotCursor;
        canvas.hoverCursor = dotCursor;
        canvas.forEachObject(o => {
          o.selectable = false;
          o.evented = false;
        });
        break;
        
      case 'text':
        canvas.isDrawingMode = false;
        canvas.selection = false;
        canvas.skipTargetFind = true;
        canvas.defaultCursor = 'text';
        canvas.hoverCursor = 'text';
        canvas.forEachObject(o => {
          o.selectable = false;
          o.evented = false;
        });
        break;
        
      case 'image':
        canvas.isDrawingMode = false;
        canvas.selection = false;
        canvas.skipTargetFind = true;
        canvas.defaultCursor = 'crosshair';
        canvas.hoverCursor = 'crosshair';
        canvas.forEachObject(o => {
          o.selectable = false;
          o.evented = false;
        });
        break;
        
      default:
        canvas.isDrawingMode = false;
        canvas.selection = canDraw;
        canvas.skipTargetFind = !canDraw;
    }
  }, [activeTool, activeColor, brushSize, canDraw, fabricRef, canvasReady]);

  // Update brush color and size
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !canvas.freeDrawingBrush || !canvasReady) return;
    
    if (activeTool === 'highlighter') {
      canvas.freeDrawingBrush.color = activeColor;
      canvas.freeDrawingBrush.width = brushSize * 4;
    } else if (activeTool === 'brush') {
      const brushColor = activeColor.startsWith('#') 
        ? activeColor + 'cc'
        : activeColor;
      canvas.freeDrawingBrush.color = brushColor;
      canvas.freeDrawingBrush.width = brushSize * 3;
    } else {
      canvas.freeDrawingBrush.color = activeColor;
      canvas.freeDrawingBrush.width = brushSize;
    }
  }, [activeColor, brushSize, activeTool, fabricRef, canvasReady]);

  // Set view-only mode
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !canvasReady) return;
    
    if (!canDraw) {
      canvas.isDrawingMode = false;
      canvas.selection = false;
      canvas.skipTargetFind = true;
      canvas.defaultCursor = 'default';
      canvas.hoverCursor = 'default';
      canvas.discardActiveObject();
      // Stop any in-progress eraser drag or shape drawing
      isErasingRef.current = false;
      isDrawingShapeRef.current = false;
      currentShapeRef.current = null;
      canvas.forEachObject(obj => {
        obj.selectable = false;
        obj.evented = false;
      });
      canvas.requestRenderAll();
    }
  }, [canDraw, fabricRef, canvasReady]);

  // Apply styles to newly added objects (including from remote sync)
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !canvasReady) return;

    const handleObjectAdded = (e) => {
      if (e.target && !e.target._isLiveStroke) {
        applyObjectStyles(e.target);
      }
    };

    canvas.on('object:added', handleObjectAdded);
    return () => canvas.off('object:added', handleObjectAdded);
  }, [fabricRef, canvasReady]);

  // Keyboard shortcuts for context menu actions
  useEffect(() => {
    if (!canDraw || !canvasReady) return;

    const handleKeyDown = (e) => {
      const canvas = fabricRef.current;
      if (!canvas) return;
      
      const active = canvas.getActiveObject();
      if (!active) return;

      // Ctrl+ArrowUp = Bring to front
      if (e.ctrlKey && e.key === 'ArrowUp') {
        e.preventDefault();
        if (typeof canvas.bringObjectToFront === 'function') {
          canvas.bringObjectToFront(active);
        } else {
          canvas.bringToFront(active);
        }
        canvas.renderAll();
        if (onBringToFrontRef.current) onBringToFrontRef.current(active);
      }
      // Ctrl+ArrowDown = Send to back
      if (e.ctrlKey && e.key === 'ArrowDown') {
        e.preventDefault();
        if (typeof canvas.sendObjectToBack === 'function') {
          canvas.sendObjectToBack(active);
        } else {
          canvas.sendToBack(active);
        }
        canvas.renderAll();
        if (onSendToBackRef.current) onSendToBackRef.current(active);
      }
      // Shift+L = Lock/unlock
      if (e.shiftKey && e.key === 'L') {
        e.preventDefault();
        const isLocked = active.lockMovementX;
        active.set({
          lockMovementX: !isLocked,
          lockMovementY: !isLocked,
          lockScalingX: !isLocked,
          lockScalingY: !isLocked,
          lockRotation: !isLocked,
          hasControls: isLocked,
        });
        canvas.renderAll();
      }
      // Delete/Backspace (but not when editing text)
      if ((e.key === 'Delete' || e.key === 'Backspace') && !active.isEditing) {
        e.preventDefault();
        if (onDeleteSelectedRef.current) onDeleteSelectedRef.current();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canDraw, canvasReady, fabricRef]);

  // Context menu handlers
  const handleContextRemove = useCallback(() => {
    if (onDeleteSelectedRef.current) onDeleteSelectedRef.current();
  }, []);

  const handleContextBringToFront = useCallback(() => {
    const canvas = fabricRef.current;
    const active = canvas?.getActiveObject();
    if (!canvas || !active) return;
    if (typeof canvas.bringObjectToFront === 'function') {
      canvas.bringObjectToFront(active);
    } else {
      canvas.bringToFront(active);
    }
    canvas.renderAll();
    if (onBringToFrontRef.current) onBringToFrontRef.current(active);
  }, [fabricRef]);

  const handleContextSendToBack = useCallback(() => {
    const canvas = fabricRef.current;
    const active = canvas?.getActiveObject();
    if (!canvas || !active) return;
    if (typeof canvas.sendObjectToBack === 'function') {
      canvas.sendObjectToBack(active);
    } else {
      canvas.sendToBack(active);
    }
    canvas.renderAll();
    if (onSendToBackRef.current) onSendToBackRef.current(active);
  }, [fabricRef]);

  const handleContextToggleLock = useCallback(() => {
    const canvas = fabricRef.current;
    const active = canvas?.getActiveObject();
    if (!canvas || !active) return;
    
    const isLocked = active.lockMovementX;
    active.set({
      lockMovementX: !isLocked,
      lockMovementY: !isLocked,
      lockScalingX: !isLocked,
      lockScalingY: !isLocked,
      lockRotation: !isLocked,
      hasControls: isLocked,
    });
    canvas.renderAll();
  }, [fabricRef]);

  const handleContextCopy = useCallback(() => {
    const canvas = fabricRef.current;
    const active = canvas?.getActiveObject();
    if (!canvas || !active) return;
    
    active.clone((cloned) => {
      cloned.set({
        left: (active.left || 0) + 20,
        top: (active.top || 0) + 20,
      });
      applyObjectStyles(cloned);
      canvas.add(cloned);
      canvas.setActiveObject(cloned);
      canvas.renderAll();
      if (onCopyWidgetRef.current) onCopyWidgetRef.current(cloned);
    });
  }, [fabricRef]);

  const handleContextResizeToFit = useCallback(() => {
    const canvas = fabricRef.current;
    const active = canvas?.getActiveObject();
    if (!canvas || !active) return;
    
    const maxW = CANVAS_WIDTH * 0.8;
    const maxH = CANVAS_HEIGHT * 0.8;
    const objW = active.getScaledWidth();
    const objH = active.getScaledHeight();
    
    const scale = Math.min(maxW / objW, maxH / objH, 1);
    active.set({
      scaleX: (active.scaleX || 1) * scale,
      scaleY: (active.scaleY || 1) * scale,
      left: (CANVAS_WIDTH - objW * scale) / 2,
      top: (CANVAS_HEIGHT - objH * scale) / 2,
    });
    active.setCoords();
    canvas.renderAll();
    
    if (active.objectId && onObjectModifiedRef.current) {
      onObjectModifiedRef.current(active, {
        left: active.left,
        top: active.top,
        scaleX: active.scaleX,
        scaleY: active.scaleY,
        angle: active.angle,
      }, null);
    }
  }, [fabricRef]);

  // Handle image file selection for the placement modal
  const handleImageFileSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file || !imagePlacement) return;
    
    if (!file.type.startsWith('image/')) return;
    
    // Formats that can carry an alpha channel
    const alphaFormats = ['image/png', 'image/webp', 'image/gif', 'image/svg+xml', 'image/avif'];
    const mayHaveAlpha = alphaFormats.includes(file.type);
    const placementPos = { ...imagePlacement };
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const cvs = document.createElement('canvas');
        const ctx = cvs.getContext('2d');
        
        const maxSize = 800;
        let { width, height } = img;
        if (width > maxSize || height > maxSize) {
          if (width > height) {
            height = (height / width) * maxSize;
            width = maxSize;
          } else {
            width = (width / height) * maxSize;
            height = maxSize;
          }
        }
        
        cvs.width = width;
        cvs.height = height;
        
        // Always clear first so transparency is preserved
        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        
        // Detect actual alpha pixels if the format supports it
        let hasAlpha = false;
        if (mayHaveAlpha) {
          const pixelData = ctx.getImageData(0, 0, Math.min(width, 200), Math.min(height, 200)).data;
          for (let i = 3; i < pixelData.length; i += 16) { // Sample every 4th pixel
            if (pixelData[i] < 250) { hasAlpha = true; break; }
          }
        }
        
        // Use PNG for transparent images, JPEG for opaque
        const useAlpha = mayHaveAlpha && hasAlpha;
        const format = useAlpha ? 'image/png' : 'image/jpeg';
        const quality = useAlpha ? undefined : 0.7;
        let base64 = cvs.toDataURL(format, quality);
        
        // Compress further if too large for data channel (~50KB limit)
        if (base64.length > 50000) {
          if (useAlpha) {
            // For transparent images: reduce dimensions to shrink, keep PNG
            const scale = Math.sqrt(50000 / base64.length) * 0.9;
            cvs.width = width * scale;
            cvs.height = height * scale;
            ctx.clearRect(0, 0, cvs.width, cvs.height);
            ctx.drawImage(img, 0, 0, cvs.width, cvs.height);
            base64 = cvs.toDataURL('image/png');
            if (base64.length > 50000) {
              cvs.width = cvs.width * 0.5;
              cvs.height = cvs.height * 0.5;
              ctx.clearRect(0, 0, cvs.width, cvs.height);
              ctx.drawImage(img, 0, 0, cvs.width, cvs.height);
              base64 = cvs.toDataURL('image/png');
            }
          } else {
            // For opaque images: try more aggressive JPEG compression first
            base64 = cvs.toDataURL('image/jpeg', 0.4);
            if (base64.length > 50000) {
              const scale = 0.5;
              cvs.width = width * scale;
              cvs.height = height * scale;
              ctx.drawImage(img, 0, 0, cvs.width, cvs.height);
              base64 = cvs.toDataURL('image/jpeg', 0.5);
            }
          }
        }
        
        if (onAddImageRef.current) {
          onAddImageRef.current(base64, cvs.width, cvs.height, placementPos.x, placementPos.y);
        }
        
        // Close modal and switch to select
        setImagePlacement(null);
        if (onToolChangeRef.current) {
          onToolChangeRef.current('select');
        }
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
    
    e.target.value = '';
  }, [imagePlacement]);

  // Cancel image placement
  const cancelImagePlacement = useCallback(() => {
    setImagePlacement(null);
    if (onToolChangeRef.current) {
      onToolChangeRef.current('select');
    }
  }, []);

  if (!fabricLoaded) {
    return (
      <div className="flex items-center justify-center w-full h-full bg-white" style={{ minWidth: '200px', minHeight: '120px' }}>
        <div className="text-gray-500">{t('whiteboard.loading', 'Loading whiteboard...')}</div>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className="whiteboard-canvas-container"
      style={{
        width: '100%',
        height: '100%',
        minWidth: '200px',
        minHeight: '120px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#1a1a1a',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#ffffff' }}>
        <canvas
          ref={canvasElRef}
          style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}
        />
      </div>

      {/* Floating text toolbar */}
      {canDraw && (
        <WhiteboardTextToolbar
          visible={textToolbar.visible}
          position={textToolbar.position}
          textObject={textToolbar.textObject}
          fabricCanvas={fabricRef.current}
          onUpdate={(props) => {
            const obj = textToolbar.textObject;
            if (obj && obj.objectId && onObjectModifiedRef.current) {
              onObjectModifiedRef.current(obj, {
                left: obj.left,
                top: obj.top,
                scaleX: obj.scaleX,
                scaleY: obj.scaleY,
                angle: obj.angle,
                ...props,
              }, null);
            }
          }}
          onClose={() => setTextToolbar({ visible: false, position: null, textObject: null })}
        />
      )}

      {/* Context menu */}
      {canDraw && (
        <WhiteboardContextMenu
          visible={contextMenu.visible}
          position={contextMenu.position}
          isLocked={contextMenu.isLocked}
          onRemove={handleContextRemove}
          onCopy={handleContextCopy}
          onBringToFront={handleContextBringToFront}
          onSendToBack={handleContextSendToBack}
          onToggleLock={handleContextToggleLock}
          onResizeToFit={handleContextResizeToFit}
          onClose={() => setContextMenu({ visible: false, position: null, isLocked: false })}
        />
      )}

      {/* Image placement modal */}
      {imagePlacement && (
        <div 
          className="absolute inset-0 z-[9999] flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0,0,0,0.3)' }}
          onClick={(e) => {
            if (e.target === e.currentTarget) cancelImagePlacement();
          }}
        >
          <div className="bg-gray-800 rounded-2xl shadow-2xl border border-gray-600 p-6 max-w-sm w-full mx-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-xl bg-cyan-600/20 flex items-center justify-center">
                  <svg className="w-5 h-5 text-cyan-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" />
                    <path d="M21 15l-5-5L5 21" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm">{t('whiteboard.imagePlacement.title', 'Insert Image')}</h3>
                  <p className="text-gray-400 text-xs">{t('whiteboard.imagePlacement.subtitle', 'Choose an image from your computer')}</p>
                </div>
              </div>
              <button
                onClick={cancelImagePlacement}
                className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
                </svg>
              </button>
            </div>
            
            {/* Upload area */}
            <label className="block cursor-pointer group">
              <div className="border-2 border-dashed border-gray-500 group-hover:border-cyan-400 rounded-xl p-8 text-center transition-colors">
                <svg className="w-12 h-12 mx-auto text-gray-400 group-hover:text-cyan-400 mb-3 transition-colors" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M12 16V4m0 0l-4 4m4-4l4 4" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M20 16v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <p className="text-gray-300 text-sm font-medium mb-1">{t('whiteboard.imagePlacement.clickToUpload', 'Click to choose image')}</p>
                <p className="text-gray-500 text-xs">{t('whiteboard.imagePlacement.formats', 'PNG, JPG, GIF, WebP — transparency supported')}</p>
              </div>
              <input
                ref={imageFileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageFileSelect}
                className="hidden"
              />
            </label>
            
            {/* Cancel button */}
            <button
              onClick={cancelImagePlacement}
              className="w-full mt-3 px-4 py-2 text-sm text-gray-400 hover:text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              {t('whiteboard.imagePlacement.cancel', 'Cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

WhiteboardCanvas.displayName = 'WhiteboardCanvas';

export default WhiteboardCanvas;
